import BrandSingleFetcher from "@/features/brands/components/single/brand.single.fetcher";
import EventSingleFetcher from "@/features/events/components/single/event.single.fetcher";
import { Job } from "@/models/Job.model";
import {
  Container,
  Grid,
  Box,
  Text,
  Flex,
  Heading,
  Card,
} from "@radix-ui/themes";
import { FC, JSX } from "react";
import DateObject from "react-date-object";
import Markdown from "react-markdown";
import JobSingleUIApply from "./job.single.ui.apply";
import { useApplyForJob } from "../../hooks/useApplyForJob";
import { useProfileJobJunction } from "../../hooks/useProfileJobJunction";

const JobSingleUI: FC<Job> = ({
  id,
  position,
  brand,
  event,
  descriptions,
  event_dates,
  application_deadline,
  compensation,
}): JSX.Element => {
  // Dates
  const startDate = new DateObject(event_dates.start.toDate());
  const endDate = new DateObject(event_dates.end.toDate());

  const { handleApply, loading, error } = useApplyForJob();

  const {
    data,
    loading: applicationStatus,
    error: applicationError,
    refetch,
  } = useProfileJobJunction(id);

  return (
    <Container px={{ initial: "4", lg: "0" }}>
      <BrandSingleFetcher slug={brand} />
      <Flex gap="1" direction="column" mb="5">
        <Heading as="h1" size="7" mb="0">
          {position}
        </Heading>
        <Text as="p" size="2" color="gray">
          {startDate.format("DD.MM.YYYY")} - {endDate.format("DD.MM.YYYY")}
        </Text>
      </Flex>
      <Grid
        gap="5"
        columns={{ initial: "1", lg: "4" }}
        pb={{ initial: "7", lg: "0" }}
      >
        <Box gridColumn={{ initial: "1 / 5", lg: "1 / 4" }}>
          <Flex direction="column" gap="5">
            <Box pt="5" pb="4" display={{ initial: "block", md: "none" }}>
              <JobSingleUIApply
                id={id}
                application_deadline={application_deadline}
                loading={loading}
                error={error}
                handleApply={handleApply}
                data={data}
                applicationStatus={applicationStatus}
                applicationError={applicationError}
                refetch={refetch}
              />
            </Box>
            <Box>
              <Heading as="h4" size="3">
                Role Description
              </Heading>
              <Markdown>{descriptions.role}</Markdown>
            </Box>
            <Box>
              <Heading as="h4" size="3">
                About the Event
              </Heading>
              <Markdown>{descriptions.event}</Markdown>
            </Box>
            <Box>
              <Heading as="h4" size="3">
                Compensation
              </Heading>
              <Text as="p" size="2" color="gray">
                {compensation}
              </Text>
            </Box>
            <Box>
              <Heading as="h4" size="3">
                Your Responsibilities
              </Heading>
              <Markdown>{descriptions.responsibilities}</Markdown>
            </Box>
            <Box>
              <Heading as="h4" size="3">
                Qualifications
              </Heading>
              <Markdown>{descriptions.qualifications}</Markdown>
            </Box>
            <Box>
              <Heading as="h4" size="3">
                What We Offer
              </Heading>
              <Markdown>{descriptions.offer}</Markdown>
            </Box>
          </Flex>
        </Box>
        <Box gridColumn={{ initial: "1 / 5", lg: "4 / 5" }}>
          <Flex direction="column" gap="3">
            <Card size="3">
              <EventSingleFetcher slug={event} />
            </Card>

            <JobSingleUIApply
              id={id}
              application_deadline={application_deadline}
              loading={loading}
              error={error}
              handleApply={handleApply}
              data={data}
              applicationStatus={applicationStatus}
              applicationError={applicationError}
              refetch={refetch}
            />
          </Flex>
        </Box>
      </Grid>
    </Container>
  );
};

export default JobSingleUI;
